import { useCallback, useEffect, useRef, useState } from 'react';
import { convertToWav } from '../lib/audio';
import { toast } from './use-toast';

// One mic stream feeds both the MediaRecorder (the take we send for analysis)
// and the live contour (what the user watches while speaking).
//
// status: 'idle' | 'recording' | 'processing' | 'ready'
export function useRecorder({ contour, maxSeconds = Infinity } = {}) {
  const [status, setStatus] = useState('idle');
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startedRef = useRef(0);

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    releaseStream();
  }, []);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const setTake = useCallback((b) => {
    setBlob(b);
    setUrl(b ? URL.createObjectURL(b) : null);
  }, []);

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    contour?.stop();
    if (recRef.current && recRef.current.state !== 'inactive') {
      setStatus('processing');
      recRef.current.stop();
    }
  }, [contour]);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast({
        title: 'Recording not supported',
        description: 'Your browser can’t record audio. Try the latest Chrome, Safari, Firefox or Edge.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        const raw = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        releaseStream();
        try {
          setTake(await convertToWav(raw));
        } catch (err) {
          console.error('WAV conversion failed, sending original format:', err);
          setTake(raw);
        }
        setStatus('ready');
      };
      recRef.current = rec;
      setTake(null);
      setSeconds(0);
      rec.start();
      contour?.start({ stream, maxSeconds: Infinity });
      startedRef.current = Date.now();
      setStatus('recording');
      timerRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - startedRef.current) / 1000);
        setSeconds(s);
        if (s >= maxSeconds) stop();
      }, 250);
    } catch (error) {
      console.error('Error starting recording:', error);
      let description = 'Please check your microphone settings.';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        description = 'Allow microphone access in your browser settings, then try again.';
      } else if (error.name === 'NotFoundError') {
        description = 'No microphone found. Connect one and try again.';
      }
      toast({ title: 'Microphone unavailable', description, variant: 'destructive' });
    }
  }, [contour, maxSeconds, setTake, stop]);

  // For uploads: the file goes through the same WAV conversion as a recording.
  const loadFile = useCallback(async (file) => {
    if (!file) return;
    setStatus('processing');
    try {
      setTake(await convertToWav(file));
      setSeconds(0);
      setStatus('ready');
    } catch (err) {
      console.error('Error converting file:', err);
      toast({ title: 'Couldn’t read that file', description: 'Try an MP3, WAV or M4A recording.', variant: 'destructive' });
      setStatus('idle');
    }
  }, [setTake]);

  const reset = useCallback(() => {
    setTake(null);
    setSeconds(0);
    setStatus('idle');
    contour?.reset();
  }, [contour, setTake]);

  return { status, seconds, blob, url, start, stop, reset, loadFile };
}
